/**
 * This module contains the logic for rendering a readonly minidoc. It uses
 * minidoc in readonly mode in order to ensure the minidoc cards
 * render properly.
 */
import { cardMiddleware, defaultToolbarActions, minidoc, minidocToolbar } from 'minidoc-editor';
import { ManualDom } from '@components/manual-dom';
import { generateMediaCard, mediaMiddleware } from '@components/media-card';
import { useDisposableMemo } from 'client/lib/hooks';
import { rawHtmlCard, embedHtmlCard } from '@components/raw-html-card';
import { useEffect } from 'preact/hooks';
import { useLinkPreviews, LinkPreviewData } from 'client/lib/use-link-previews';

type Props = {
  id?: any;
  content?: string;
  shouldRenderPdfViewer?: boolean;
  hideCaptions?: boolean;
  class?: string;
  enableLinkPreviews?: boolean;
};

function slugToTitle(slug: string) {
  return slug
    .replace(/--[a-f0-9-]{36}$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettifyBareLink(el: HTMLAnchorElement, origin: string) {
  const text = el.textContent?.trim() || '';
  const href = el.href;
  if (!text || !href) {
    return;
  }

  const isTextTheUrl =
    text === href ||
    text === href.replace(/\/$/, '') ||
    text === decodeURIComponent(href) ||
    text === decodeURIComponent(href).replace(/\/$/, '');
  if (!isTextTheUrl) {
    return;
  }

  const isInternal = href.startsWith(origin);
  if (isInternal) {
    const path = href.slice(origin.length);
    const lessonMatch = path.match(/\/courses\/[^/]+\/lessons\/([^/?#]+)/);
    const courseMatch = path.match(/\/courses\/([^/?#]+)/);

    let label: string | undefined;
    let icon = '';
    if (lessonMatch) {
      label = slugToTitle(lessonMatch[1]);
      icon = '▶ ';
    } else if (courseMatch) {
      label = slugToTitle(courseMatch[1]);
      icon = '📚 ';
    }

    if (label) {
      el.textContent = `${icon}${label}`;
      el.classList.add('pretty-link', 'pretty-link-internal');
      el.title = decodeURIComponent(href);
      return;
    }
  }

  try {
    const url = new URL(href);
    const domain = url.hostname.replace(/^www\./, '');
    let path = url.pathname;
    if (path.length > 30) {
      path = path.slice(0, 27) + '…';
    }
    el.textContent = `${domain}${path !== '/' ? path : ''}`;
    el.classList.add('pretty-link', 'pretty-link-external');
    el.title = decodeURIComponent(href);
  } catch {
    // not a valid URL, leave as-is
  }
}

function createLinkPreviewCard(preview: LinkPreviewData): HTMLElement {
  const card = document.createElement('a');
  card.href = preview.url;
  card.target = '_blank';
  card.rel = 'noopener noreferrer';
  card.className = 'link-preview-card';

  if (preview.imageUrl && /^https?:\/\//i.test(preview.imageUrl)) {
    const imgWrap = document.createElement('div');
    imgWrap.className = 'link-preview-image';
    const img = document.createElement('img');
    img.src = preview.imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    img.onerror = () => {
      imgWrap.remove();
    };
    imgWrap.appendChild(img);
    card.appendChild(imgWrap);
  }

  const body = document.createElement('div');
  body.className = 'link-preview-body';

  const siteName =
    preview.siteName ||
    (() => {
      try {
        return new URL(preview.url).hostname.replace(/^www\./, '');
      } catch {
        return '';
      }
    })();
  if (siteName) {
    const site = document.createElement('div');
    site.className = 'link-preview-site';
    try {
      const domain = new URL(preview.url).hostname;
      const favicon = document.createElement('img');
      favicon.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      favicon.alt = '';
      favicon.width = 14;
      favicon.height = 14;
      favicon.className = 'link-preview-favicon';
      favicon.onerror = () => {
        favicon.remove();
      };
      site.appendChild(favicon);
    } catch {
      // skip favicon
    }
    site.appendChild(document.createTextNode(siteName));
    body.appendChild(site);
  }

  if (preview.title) {
    const title = document.createElement('div');
    title.className = 'link-preview-title';
    title.textContent = preview.title;
    body.appendChild(title);
  }

  if (preview.description) {
    const desc = document.createElement('div');
    desc.className = 'link-preview-desc';
    desc.textContent = preview.description;
    body.appendChild(desc);
  }

  card.appendChild(body);
  return card;
}

function injectLinkPreviews(root: HTMLElement, previews: Record<string, LinkPreviewData>) {
  root.querySelectorAll('.link-preview-card').forEach((el) => el.remove());
  root.querySelectorAll<HTMLAnchorElement>('a.pretty-link-external').forEach((el) => {
    el.classList.remove('hidden');
  });

  root.querySelectorAll<HTMLAnchorElement>('a.pretty-link-external').forEach((el) => {
    const preview = previews[el.href];
    if (!preview) {
      return;
    }
    el.classList.add('hidden');
    const card = createLinkPreviewCard(preview);
    const block = el.closest('p, div, li, blockquote') || el.parentElement;
    if (block && block.parentElement) {
      block.parentElement.insertBefore(card, block.nextSibling);
    }
  });
}

/**
 * Create a minidoc instance to render the read-only view.
 */
export function createReadonlyMinidoc({
  content = '',
  shouldRenderPdfViewer,
  hideCaptions,
}: {
  content?: string;
  shouldRenderPdfViewer?: boolean;
  hideCaptions?: boolean;
}) {
  return minidoc({
    /**
     * This is the document markup as a string.
     */
    doc: content,
    /**
     * This is a student view / guide preview. No editing!
     */
    readonly: true,
    /**
     * This is the minidoc middleware we're using.
     */
    middleware: [
      /**
       * Here is where we register the cards we want to use. Media is for files.
       * In the future, we'll register surveys, quizes, etc here.
       */
      cardMiddleware([
        generateMediaCard({
          shouldRenderPdfViewer,
          hideCaptions,
        }),
        rawHtmlCard,
        embedHtmlCard,
      ]),
      /**
       * Initialize the editor with textcolor support... We may want to do this
       * without requiring the entire toolbar middlware.
       */
      minidocToolbar(defaultToolbarActions.filter((a) => a.id === 'textcolor')),
      /**
       * This extends the editor to have an `insertMedia` method which is used
       * by the media toolbar action.
       */
      mediaMiddleware(),
    ],
  });
}

/**
 * The minidoc render component manages rendering a minidoc in read-only / student view.
 */
export function ReadonlyMinidoc({
  id,
  content,
  shouldRenderPdfViewer,
  hideCaptions,
  class: className = '',
  enableLinkPreviews,
}: Props) {
  const editor = useDisposableMemo(() => {
    const result = createReadonlyMinidoc({
      content,
      shouldRenderPdfViewer,
      hideCaptions,
    });
    const { origin } = window.location;

    result.root.querySelectorAll('a').forEach((el) => {
      const { href } = el;
      if (!href.startsWith(origin) || href.startsWith(`${origin}/files/`)) {
        el.target = '_blank';
        el.rel = 'noopener noreferrer';
      }
      prettifyBareLink(el, origin);
    });

    return result;
  }, [id]);

  const previews = useLinkPreviews(enableLinkPreviews ? content : undefined);

  useEffect(() => {
    if (previews && Object.keys(previews).length > 0) {
      injectLinkPreviews(editor.root, previews);
    }
  }, [previews, editor]);

  return (
    <ManualDom
      el={editor.root}
      class={`minidoc-content prose prose-indigo dark:prose-invert font-studentcontent ${className}`}
    />
  );
}
